import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { CohortMultiSelect } from "@/components/CohortMultiSelect";
import { StatusBadge } from "@/components/StatusBadge";
import {
  CheckCircle2, Clock, Inbox, ArrowUpRight, RefreshCw, Loader2, FileCheck2, Hourglass, LogOut, Download,
  PauseCircle, PlayCircle,
} from "lucide-react";
import {
  ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { Link, useNavigate } from "react-router-dom";
import { listRegistrations, getCohortStats, listCohorts, downloadCohortBucketCsv, updateRegistrationStatus } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { downloadRegistrationsSummaryPdf } from "@/lib/pdfExport";

const emptyTotals = () => ({
  total_offer_letters: 0,
  not_started: 0,
  in_progress: 0,
  submitted: 0,
  approved: 0,
  withdrawn: 0,
  withdrawn_admission_level: 0,
  withdrawn_course_level: 0,
});

/** Collapse specialization out of cohort labels — same grouping as the Overview cards. */
const collapseProgramStats = (cohortStats) => {
  const groups = new Map();
  for (const c of cohortStats || []) {
    const label = (c.label || "").replace(/\s*\([^)]*\)/, "");
    const existing = groups.get(label);
    if (existing) {
      existing.total_offer_letters += c.total_offer_letters || 0;
      existing.not_started += c.not_started || 0;
      existing.in_progress += c.in_progress || 0;
      existing.submitted += c.submitted || 0;
      existing.approved += c.approved || 0;
      existing.withdrawn += c.withdrawn || 0;
      existing.withdrawn_admission_level += c.withdrawn_admission_level || 0;
      existing.withdrawn_course_level += c.withdrawn_course_level || 0;
    } else {
      groups.set(label, {
        label,
        total_offer_letters: c.total_offer_letters || 0,
        not_started: c.not_started || 0,
        in_progress: c.in_progress || 0,
        submitted: c.submitted || 0,
        approved: c.approved || 0,
        withdrawn: c.withdrawn || 0,
        withdrawn_admission_level: c.withdrawn_admission_level || 0,
        withdrawn_course_level: c.withdrawn_course_level || 0,
      });
    }
  }
  return Array.from(groups.values());
};

const sumStats = (rows) =>
  (rows || []).reduce((acc, c) => {
    Object.keys(acc).forEach((k) => {
      acc[k] += c[k] || 0;
    });
    return acc;
  }, emptyTotals());
const Kpi = ({ icon: Icon, label, value, color = "text-[#111]", bg = "bg-[#FBE7EA]", iconColor = "text-[#B60F2A]" }) => (
  <motion.div
    whileHover={{ y: -3 }}
    transition={{ duration: 0.2 }}
    className="mu-card p-5 text-left w-full"
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className={`h-10 w-10 rounded-xl ${bg} flex items-center justify-center`}>
      <Icon className={`h-5 w-5 ${iconColor}`} />
    </div>
    <div className={`mt-4 text-3xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-[#6B7280] mt-1 uppercase tracking-wider font-semibold">{label}</div>
  </motion.div>
);

// Withdrawn splits into two types: ADMISSION-level (withdrawn before ever
// being approved — covers not-started/in-progress/submitted) vs COURSE-level
// (approved, then later withdrawn). Total stays the headline number; the
// breakdown shows underneath so both are visible without a drill-down.
const WithdrawnKpi = ({ total, admissionLevel, courseLevel, testId }) => (
  <motion.div
    whileHover={{ y: -3 }}
    transition={{ duration: 0.2 }}
    className="mu-card p-5 text-left w-full"
    data-testid={testId}
  >
    <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center">
      <LogOut className="h-5 w-5 text-red-600" />
    </div>
    <div className="mt-4 text-3xl font-bold text-[#111]">{total}</div>
    <div className="text-xs text-[#6B7280] mt-1 uppercase tracking-wider font-semibold">Withdrawn</div>
    <div className="text-[10px] text-[#9CA3AF] mt-1.5">
      Admission: <span className="font-semibold text-[#6B7280]">{admissionLevel}</span> · Course: <span className="font-semibold text-[#6B7280]">{courseLevel}</span>
    </div>
  </motion.div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const { session } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cohortIds, setCohortIds] = useState([]);
  const [cohortStats, setCohortStats] = useState([]);
  const [cohortStatsLoading, setCohortStatsLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [exportingBucket, setExportingBucket] = useState(null); // 'not_started' | 'in_progress'
  const [statusSaving, setStatusSaving] = useState(null); // { id, status } while hold/release in flight

  // Finance/Check-in each have their own narrow, list-only page — no access
  // to this dashboard.
  useEffect(() => {
    if (session?.role === "finance") nav("/admin/finance", { replace: true });
    if (session?.role === "checkin") nav("/admin/checkin", { replace: true });
  }, [session?.role, nav]);

  const role = session?.role || "";
  const isSuperadmin = role === "superadmin";
  const isRoadmin = role === "roadmin" || isSuperadmin;
  const isAdmissions = role === "admissions";

  const load = async () => {
    setLoading(true);
    // Admissions is overview/report-only — skip recent registrations (PII list API).
    if (!isAdmissions) {
      const data = await listRegistrations({ page: 1, page_size: 6 });
      setRows(data.items || []);
    } else {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmissions]);

  // Per-cohort breakdown — shown as its own row, never summed together.
  // Polled every 20s (same cadence as Applications/Finance) so approvals,
  // fee clearances etc. show up here without a manual refresh.
  useEffect(() => {
    if (!cohortIds.length) {
      setCohortStats([]);
      return undefined;
    }
    let cancelled = false;
    const fetchStats = (silent) => {
      if (!silent) setCohortStatsLoading(true);
      getCohortStats(cohortIds).then((data) => {
        if (!cancelled) {
          setCohortStats(Array.isArray(data) ? data : []);
          if (!silent) setCohortStatsLoading(false);
        }
      });
    };
    fetchStats(false);
    const iv = setInterval(() => fetchStats(true), 20000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [cohortIds]);

  // Overview shows PROGRAM-wise stats, not specialization-wise — cohort_stats
  // returns one row per cohort (student_category_id = entity+program+
  // specialization), whose label ends "...Program (Specialization) · Year".
  // Collapse specialization out of the grouping key/label and sum the numeric
  // fields across every cohort that shares the same entity+program+year, so
  // e.g. B.Tech (AI) and B.Tech (Aerospace) show as one combined "B.Tech" card
  // instead of two identical-looking specialization cards. Cohort-level
  // granularity is preserved everywhere else (Finance, Applications filter,
  // slot groups) — this collapsing is purely a display grouping here.
  const programStats = useMemo(() => collapseProgramStats(cohortStats), [cohortStats]);

  // Combined total across every currently-selected cohort — separate from,
  // and shown alongside, the per-cohort breakdown which is never summed there.
  const overallStats = useMemo(() => {
    if (!cohortStats.length) return null;
    return sumStats(cohortStats);
  }, [cohortStats]);

  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      const cohorts = await listCohorts();
      const ids = (Array.isArray(cohorts) ? cohorts : [])
        .map((c) => c.cohort_id)
        .filter((id) => id != null);
      if (!ids.length) {
        toast.error("No cohorts found to include in the report");
        return;
      }
      const stats = await getCohortStats(ids);
      if (!Array.isArray(stats) || !stats.length) {
        toast.error("Could not load cohort stats for the report");
        return;
      }
      await downloadRegistrationsSummaryPdf(stats);
      toast.success(`Downloaded PDF for ${stats.length} cohort${stats.length === 1 ? "" : "s"}`);
    } catch (_e) {
      toast.error("Failed to download overview report");
    } finally {
      setDownloadingReport(false);
    }
  };

  const downloadBucketCsv = async (bucket) => {
    if (!cohortIds.length) {
      toast.error("Select at least one cohort to export");
      return;
    }
    setExportingBucket(bucket);
    try {
      const res = await downloadCohortBucketCsv(cohortIds, bucket);
      if (!res.ok) {
        toast.error(res.data?.detail || "Could not download list — please try again");
        return;
      }
      toast.success(
        bucket === "not_started"
          ? "Downloaded Not Started students CSV"
          : bucket === "in_progress"
            ? "Downloaded In Progress students CSV"
            : "Downloaded Submitted (pending approval) students CSV"
      );
    } catch (_e) {
      toast.error("Failed to download student list");
    } finally {
      setExportingBucket(null);
    }
  };
  const apps = useMemo(() => rows.map((r) => ({
    id: r.application_number,
    applicationNumber: r.application_number,
    ackNumber: r.ack_number,
    rollNumber: r.roll_number,
    name: r.candidate?.name || "—",
    email: r.candidate?.email || "",
    photo: r.photograph?.file_url || r.candidate?.photo_url || `https://ui-avatars.com/api/?background=B60F2A&color=fff&name=${encodeURIComponent(r.candidate?.name || "N A")}`,
    status: r.status || "draft",
    tokenNumber: r.token_number ?? null,
    submittedAt: r.submitted_at || r.updated_at || r.created_at,
  })), [rows]);

  const recent = apps.slice(0, 6);

  const canPutOnHold = (a) => isRoadmin && a.status === "submitted" && a.tokenNumber != null;
  const canReleaseHold = (a) => isRoadmin && a.status === "on_hold";

  const setHoldStatus = async (appNo, newStatus, prevStatus) => {
    setStatusSaving({ id: appNo, status: newStatus });
    const res = await updateRegistrationStatus(appNo, { actor_role: role, acting_as: "roadmin", status: newStatus });
    setStatusSaving(null);
    if (res) {
      setRows((prev) =>
        prev.map((r) => (r.application_number === appNo ? { ...r, status: res.status || newStatus } : r))
      );
      toast.success(
        newStatus === "on_hold"
          ? "Application put On Hold"
          : prevStatus === "on_hold" && newStatus === "submitted"
            ? "On Hold released"
            : "Status updated"
      );
    } else {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F7F7F7]">
      <DashboardSidebar variant="admin" />
      <main className="flex-1 p-6 lg:p-10 max-w-7xl w-full">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="tiny-label">Admin Console</div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#111] tracking-tight mt-1">Admissions Overview</h1>
            <p className="text-[#6B7280] mt-1">
              {isAdmissions
                ? "Select cohorts to view stats, download the summary report, or export Not Started / In Progress / Submitted student lists."
                : "Select a cohort to see its stats."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <CohortMultiSelect selected={cohortIds} onChange={setCohortIds} />
            {isAdmissions && (
              <>
                <button
                  type="button"
                  onClick={() => downloadBucketCsv("not_started")}
                  disabled={!cohortIds.length || exportingBucket != null}
                  className="mu-btn-secondary text-sm"
                  data-testid="btn-export-not-started"
                  title="CSV: application_number, name, mail_id, phone_number"
                >
                  {exportingBucket === "not_started"
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Download className="h-4 w-4" />}
                  Not Started CSV
                </button>
                <button
                  type="button"
                  onClick={() => downloadBucketCsv("in_progress")}
                  disabled={!cohortIds.length || exportingBucket != null}
                  className="mu-btn-secondary text-sm"
                  data-testid="btn-export-in-progress"
                  title="CSV: application_number, name, mail_id, phone_number"
                >
                  {exportingBucket === "in_progress"
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Download className="h-4 w-4" />}
                  In Progress CSV
                </button>
                <button
                  type="button"
                  onClick={() => downloadBucketCsv("submitted")}
                  disabled={!cohortIds.length || exportingBucket != null}
                  className="mu-btn-secondary text-sm"
                  data-testid="btn-export-submitted"
                  title="CSV: submitted registrations with assigned slots, pending approval"
                >
                  {exportingBucket === "submitted"
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Download className="h-4 w-4" />}
                  Submitted CSV
                </button>
              </>
            )}
            <button
              type="button"
              onClick={downloadReport}
              disabled={downloadingReport}
              className="mu-btn-secondary text-sm"
              data-testid="btn-download-overview-report"
            >
              {downloadingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download report
            </button>
            <button onClick={load} className="mu-btn-secondary text-sm" data-testid="btn-refresh-dashboard">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
            {isAdmissions ? (
              <Link to="/admin/applications" className="mu-btn-primary" data-testid="btn-view-all-apps">
                View all applications <ArrowUpRight className="h-4 w-4" />
              </Link>
            ) : (
              <Link to="/admin/registrations" className="mu-btn-primary" data-testid="btn-view-all-apps">
                View all registrations <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>

        {cohortIds.length === 0 ? (
          <div className="mu-card p-10 mt-8 text-center text-[#6B7280] text-sm" data-testid="cohort-stats-empty">
            Select a cohort above to see its stats.
          </div>
        ) : (
          <div className="mt-8" data-testid="cohort-stats-section">
            <div className="tiny-label">Per-program breakdown — shown separately</div>
            {cohortStatsLoading ? (
              <div className="mu-card p-8 mt-2 flex items-center justify-center text-[#9CA3AF] text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading cohort stats…
              </div>
            ) : (
              <>
                {overallStats && (
                  <div className="mu-card p-5 mt-2 border-[#111]" data-testid="cohort-stats-overall">
                    <h3 className="text-sm font-semibold text-[#111] mb-3">
                      Overall — {programStats.length} program{programStats.length > 1 ? "s" : ""} combined
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      <Kpi icon={FileCheck2} label="Offer Letters" value={overallStats.total_offer_letters} bg="bg-blue-50" iconColor="text-blue-600" />
                      <Kpi icon={Hourglass} label="Not Started" value={overallStats.not_started} bg="bg-slate-100" iconColor="text-slate-600" />
                      <Kpi icon={Clock} label="In Progress" value={overallStats.in_progress} bg="bg-amber-50" iconColor="text-amber-600" />
                      <Kpi icon={Inbox} label="Submitted" value={overallStats.submitted} bg="bg-blue-50" iconColor="text-blue-600" />
                      <Kpi icon={CheckCircle2} label="Approved" value={overallStats.approved} bg="bg-green-50" iconColor="text-green-600" />
                      <WithdrawnKpi
                        total={overallStats.withdrawn}
                        admissionLevel={overallStats.withdrawn_admission_level}
                        courseLevel={overallStats.withdrawn_course_level}
                        testId="kpi-withdrawn-overall"
                      />
                    </div>
                  </div>
                )}

                {programStats.length > 1 && (
                  <div className="mu-card p-6 mt-2" data-testid="cohort-comparison-chart">
                    <h3 className="text-sm font-semibold text-[#111] mb-4">Offer letters vs. approved, by program</h3>
                    <div className="h-64">
                      <ResponsiveContainer>
                        <BarChart data={programStats.map((c) => ({ name: c.label, "Offer Letters": c.total_offer_letters, Submitted: c.submitted, Approved: c.approved }))} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke="#F3F4F6" />
                          <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <Tooltip contentStyle={{ background: "#111", border: "none", borderRadius: 10, color: "#fff", fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar dataKey="Offer Letters" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Submitted" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Approved" fill="#10B981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                <div className="space-y-4 mt-4">
                  {programStats.map((c) => (
                    <div key={c.label} className="mu-card p-5" data-testid={`program-stats-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <h3 className="text-sm font-semibold text-[#111] mb-3">{c.label}</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Kpi icon={FileCheck2} label="Offer Letters" value={c.total_offer_letters} bg="bg-blue-50" iconColor="text-blue-600" />
                        <Kpi icon={Hourglass} label="Not Started" value={c.not_started} bg="bg-slate-100" iconColor="text-slate-600" />
                        <Kpi icon={Clock} label="In Progress" value={c.in_progress} bg="bg-amber-50" iconColor="text-amber-600" />
                        <Kpi icon={Inbox} label="Submitted" value={c.submitted} bg="bg-blue-50" iconColor="text-blue-600" />
                        <Kpi icon={CheckCircle2} label="Approved" value={c.approved} bg="bg-green-50" iconColor="text-green-600" />
                        <WithdrawnKpi
                          total={c.withdrawn}
                          admissionLevel={c.withdrawn_admission_level}
                          courseLevel={c.withdrawn_course_level}
                          testId={`kpi-withdrawn-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {!isAdmissions && (
        <div className="mu-card mt-6 overflow-hidden" data-testid="recent-applications">
          <div className="p-6 flex items-center justify-between border-b border-[#E5E7EB]">
            <div>
              <div className="tiny-label">Latest</div>
              <h3 className="text-lg font-semibold text-[#111]">Recent registrations</h3>
            </div>
            <Link to="/admin/registrations" className="text-sm text-[#B60F2A] hover:underline inline-flex items-center gap-1">
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F7F7] text-[#6B7280] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Candidate</th>
                  <th className="text-left px-6 py-3 font-semibold">Application No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Status</th>
                  <th className="text-left px-6 py-3 font-semibold">Submitted</th>
                  <th className="text-right px-6 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {loading ? (
                  <tr><td colSpan={5} className="px-6 py-16 text-center text-[#9CA3AF]"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…</td></tr>
                ) : recent.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-16 text-center text-[#9CA3AF]">No applications yet.</td></tr>
                ) : recent.map((a) => (
                  <tr key={a.id} className="hover:bg-[#F7F7F7]/60 transition" data-testid={`recent-row-${a.id}`}>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <img src={a.photo} alt={a.name} className="h-8 w-8 rounded-full object-cover" />
                        <div>
                          <div className="text-[#111] font-medium">{a.name}</div>
                          <div className="text-xs text-[#6B7280]">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-[#111] font-medium">{a.applicationNumber}</td>
                    <td className="px-6 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-6 py-3 text-[#6B7280]">{a.submittedAt ? new Date(a.submittedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex items-center justify-end gap-3 flex-wrap">
                        {canPutOnHold(a) && (
                          <button
                            type="button"
                            onClick={() => setHoldStatus(a.id, "on_hold", a.status)}
                            disabled={!!statusSaving}
                            className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm font-medium disabled:opacity-50"
                            data-testid={`btn-on-hold-${a.id}`}
                            title="Student not available at slot"
                          >
                            {statusSaving?.id === a.id && statusSaving?.status === "on_hold"
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <PauseCircle className="h-3.5 w-3.5" />}
                            Put on hold
                          </button>
                        )}
                        {canReleaseHold(a) && (
                          <button
                            type="button"
                            onClick={() => setHoldStatus(a.id, "submitted", a.status)}
                            disabled={!!statusSaving}
                            className="inline-flex items-center gap-1 text-[#B60F2A] hover:underline text-sm font-medium disabled:opacity-50"
                            data-testid={`btn-release-hold-${a.id}`}
                          >
                            {statusSaving?.id === a.id && statusSaving?.status === "submitted"
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <PlayCircle className="h-3.5 w-3.5" />}
                            Release hold
                          </button>
                        )}
                        <Link to={`/admin/registrations/${encodeURIComponent(a.id)}`} className="text-[#B60F2A] hover:underline text-sm font-medium">View</Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
